import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isChunkLoadError } from '../lib/chunkError.js';

// Audit שפ-02 (2026-08-16): there was no error boundary anywhere in 38,000
// lines of client. One thrown error inside one component unmounted the whole
// tree and left a blank white page — no message, no way back, nothing in the
// UI to report.
//
// In the office that costs an F5. At a station on the floor, in Hebrew or in
// Russian, a white screen reads as "the system is down": the worker stops, goes
// looking for a supervisor, and the shift goes back to paper. That is the
// failure this catches — not the bug itself, but the bug turning into a work
// stoppage.
//
// Deliberately a plain class component with no dependencies and no data
// fetching: whatever just broke, this must still render.

type Props = {
  children: ReactNode;
  /**
   * בתוך המעטפת (02/11/2026): כרטיס בתוך אזור התוכן, לא מסך מלא — התפריט
   * נשאר, ואפשר ללכת למסך אחר.
   */
  inline?: boolean;
  /** כשהערך משתנה (מעבר מסך) השגיאה נמחקת והמסך החדש מצויר. */
  resetKey?: string;
};
type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidUpdate(prev: Props): void {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The server log is where an incident gets reconstructed from, but this
    // failure happened in a browser. The console is what the person on the
    // phone can be walked into, and what a screenshot captures.
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  private readonly reload = (): void => {
    window.location.reload();
  };

  private readonly goHome = (): void => {
    window.location.href = '/app';
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const outer = this.props.inline
      ? 'flex items-center justify-center py-10'
      : 'min-h-screen supports-[height:100dvh]:min-h-dvh flex items-center justify-center bg-navy-50 p-6';

    // ── קובץ מסך שלא נטען (02/11/2026) ──────────────────────────────────
    // מאז שכל מסך בקובץ משלו, לשונית שנפתחה לפני עדכון מבקשת קובץ שנמחק.
    // זו אינה תקלה במסך: הגרסה התחלפה. אומרים את זה, ובעברית וברוסית —
    // הקיוסקים ברצפה הם הלשוניות שפתוחות הכי הרבה זמן.
    if (isChunkLoadError(error)) {
      return (
        <div dir="rtl" className={outer} data-testid="chunk-reload">
          <div className="bg-white rounded-2xl border border-navy-100 max-w-md w-full p-7 text-center">
            <h1 className="text-xl font-bold text-navy-900 mb-2">גרסה חדשה של המערכת עלתה</h1>
            <p className="text-[15px] text-navy-600 leading-relaxed mb-1">
              המסך הזה לא נטען כי המערכת עודכנה. טען מחדש — שום דבר לא אבד.
            </p>
            <p className="text-[15px] text-navy-500 leading-relaxed mb-5" dir="ltr" lang="ru">
              Система обновилась. Нажмите «Загрузить заново».
            </p>
            <button
              type="button"
              onClick={this.reload}
              className="w-full px-4 py-2.5 bg-navy-600 text-white rounded-lg text-[15px] font-bold hover:bg-navy-700 transition-colors"
            >
              טען מחדש · Загрузить заново
            </button>
            <p className="mt-4 text-[13px] text-navy-400">אם זה חוזר אחרי טעינה — בדוק את החיבור לרשת.</p>
          </div>
        </div>
      );
    }

    return (
      <div dir="rtl" className={outer}>
        <div className="bg-white rounded-2xl shadow-xl border border-navy-100 max-w-md w-full p-7 text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center">
            {/* Inline SVG, not an icon import — an icon library that failed to
                load is itself a reason this screen would be showing. */}
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#EE6C1F" strokeWidth="2"
                 strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>

          <h1 className="text-xl font-bold text-navy-900 mb-2">המסך הזה נתקע</h1>
          <p className="text-[15px] text-navy-600 leading-relaxed mb-1">
            שאר המערכת עובדת. הנתונים שלך שמורים — שום דבר לא אבד.
          </p>
          <p className="text-[15px] text-navy-600 leading-relaxed mb-5">
            טען מחדש, ואם זה חוזר — צלם את המסך ושלח לאלי.
          </p>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={this.reload}
              className="flex-1 px-4 py-2.5 bg-navy-600 text-white rounded-lg text-sm font-semibold hover:bg-navy-700 transition-colors"
            >
              טען מחדש
            </button>
            <button
              type="button"
              onClick={this.goHome}
              className="px-4 py-2.5 border border-navy-200 text-navy-700 rounded-lg text-sm font-semibold hover:bg-navy-50 transition-colors"
            >
              למסך הראשי
            </button>
          </div>

          {/* The message itself, small and last: useless to the worker, and the
              first thing whoever fixes it will ask for. */}
          <p className="mt-5 pt-4 border-t border-navy-100 text-[12px] text-navy-400 font-mono break-words" dir="ltr">
            {error.message || String(error)}
          </p>
        </div>
      </div>
    );
  }
}
